import React, { useContext } from "react";
import { Link } from "react-router-dom";
import FIcon from "../FIcon";

import { signOut } from "@api/user";
import MembershipDropdown from "./components/MembershipDropdown";
import { FDropdownItem } from "@types";
import useAuth from "@hooks/useAuth";
import { EVENTS } from "@utils/mixpanel_utilities";
import { MixpanelContext } from "@hooks/MixpanelProvider";
import { Role } from "@constants/roles";
import { getHelpDeskUrl } from "./helpDesk";
import ShareLinksDropdown from "./components/ShareLinksDropdown";
import { size } from "lodash";

const UserMenuDropdown = ({ isOpenLink = false }) => {
  const { hasRole, user } = useAuth();

  const { trackEvent } = useContext(MixpanelContext);

  const handleSignOut = () => {
    // Track sign-out event before actually signing out
    trackEvent(EVENTS.AUTH.SIGNED_OUT, {
      user_id: user?.id,
      brand_id: user?.brand_id,
      user_type: user?.email?.endsWith("fohr.co")
        ? "fohr"
        : user?.brand_id
          ? "brand"
          : "creator",
      role: user?.role,
    });

    // Proceed with sign-out
    signOut();
  };

  const items: FDropdownItem[] = [
    {
      name: "Home",
      icon: { name: "home", color: "black" },
      link: "/home",
    },
    {
      name: "Settings",
      icon: { name: "settings", color: "black" },
      link: "/settings",
      onClick: () => {
        trackEvent(EVENTS.USER_MENU.SETTINGS_CLICKED);
      },
    },
    !hasRole(Role.INFLUENCER)
      ? {
          name: "Education Center",
          icon: { name: "star", color: "black" },
          link: "http://fohr.in/fohr-education-center",
          target: "_blank",
          onClick: () => {
            trackEvent(EVENTS.USER_MENU.EDUCATION_CENTER_CLICKED);
          },
        }
      : null,
    {
      name: "Help Desk",
      icon: { name: "help", color: "black" },
      link: getHelpDeskUrl(user?.role || ""),
      target: "_blank",
      onClick: () => {
        trackEvent(EVENTS.USER_MENU.HELP_DESK_CLICKED, {
          help_desk_url: getHelpDeskUrl(user?.role || ""),
          user_role: user?.role,
        });
      },
    },
    {
      name: "Logout",
      icon: { name: "sign_out", color: "red" },
      onClick: handleSignOut,
    },
  ]
    .filter(Boolean)
    .filter((item) => {
      return !isOpenLink || item.name === "Logout";
    });

  const { first_name, last_name } = user;

  return (
    <>
      <div className="px-[16px] py-[12px] pb-[8px] border-b border-light_border">
        <p className="text-lg font-medium truncate">
          {`${first_name} ${last_name}`}
        </p>
        <div className="relative">
          <div className="flex items-center">
            <div className="absolute left-0 pointer-events-none">
              <FIcon
                icon="store"
                color="#000"
                size={14}
                className="-mt-[2px]"
              />
            </div>
            <MembershipDropdown />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <FIcon icon="sort-down" size={12} color="#000" />
            </div>
          </div>
        </div>
        {size(user.shared_links) > 1 ? (
          <div className="relative mt-2">
            <ShareLinksDropdown />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <FIcon icon="sort-down" size={12} color="#000" />
            </div>
          </div>
        ) : null}
      </div>
      <ul className="mt-[5px] list-unstyled">
        {items.map((item, index) => (
          <li
            key={index}
            className={`py-[10px] relative ${
              items.length > 1 && index === items.length - 1
                ? "border-t border-light_border mt-[5px]"
                : ""
            } ${!item.children ? "cursor-pointer hover:bg-neutral_100" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (item.onClick) item.onClick();
            }}
          >
            {item.link ? (
              <Link
                to={item.link}
                target={item.target}
                className="absolute inset-0"
              />
            ) : null}
            <div className="flex flex-row items-center gap-[10px] px-[16px]">
              <FIcon
                icon={item.icon.name}
                color={item.icon.color}
                size={14}
                className="-mt-[2px]"
              />
              <p className="text-[14px]">{item.name}</p>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export default UserMenuDropdown;
